import React from 'react';
import { View, Text, StyleSheet, StyleProp, TextStyle } from 'react-native';
import type { ThemeColors } from '../../context/ThemeContext';
import type { HelpPlatform, HelpTopic } from '../../help/helpTopics';
import { richParts, topicLines } from '../../help/helpSearch';

/**
 * The Help guide's pieces with no state of their own (HelpCenterModal holds the search and which
 * topic is open), so a test can call them and read the text they return.
 */

/** A line with its **on-screen names** in bold. */
export function HelpRichText({ text, style, boldStyle }: { text: string; style?: StyleProp<TextStyle>; boldStyle?: StyleProp<TextStyle> }) {
  return (
    <Text style={style}>
      {richParts(text).map((part, i) => (part.bold
        ? <Text key={i} style={[helpStyles.bold, boldStyle]}>{part.text}</Text>
        : part.text))}
    </Text>
  );
}

/** An open topic: its numbered steps, then what happens and the rules. */
export function HelpTopicBody({ topic, platform, colors }: { topic: HelpTopic; platform: HelpPlatform; colors: ThemeColors }) {
  const { steps, notes } = topicLines(topic, platform);
  const text = { color: colors.textPrimary };
  const muted = { color: colors.textSecondary };
  return (
    <View style={helpStyles.body} testID={`help-topic-body-${topic.id}`}>
      {steps.map((step, i) => (
        <View key={`s${i}`} style={helpStyles.row}>
          <View style={[helpStyles.number, { backgroundColor: colors.primaryLight }]}>
            <Text style={[helpStyles.numberText, { color: colors.primary }]}>{i + 1}</Text>
          </View>
          <HelpRichText text={step} style={[helpStyles.line, text]} boldStyle={text} />
        </View>
      ))}
      {notes.map((note, i) => (
        <View key={`n${i}`} style={helpStyles.row}>
          <Text style={[helpStyles.bullet, muted]}>•</Text>
          <HelpRichText text={note} style={[helpStyles.line, muted]} boldStyle={text} />
        </View>
      ))}
    </View>
  );
}

const helpStyles = StyleSheet.create({
  body: { gap: 8, paddingTop: 4, paddingBottom: 6 },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  number: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  numberText: { fontSize: 12, fontWeight: '800' },
  bullet: { width: 22, textAlign: 'center', fontSize: 14, lineHeight: 20 },
  line: { flex: 1, fontSize: 13.5, lineHeight: 20 },
  bold: { fontWeight: '700' },
});
